import type { ActionFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import invariant from 'tiny-invariant';
import type {
    CopyInspirationItemsMutation,
    CopyInspirationItemsMutationVariables,
} from 'generated/graphql';
import { CopyInspirationItemsDocument } from 'generated/graphql';

export const COPY_BUILD_HUB_INSPIRATION_ITEMS_API_ROUTE =
    '/remix-api/app/build-hub-inspiration-items-copy';

const DEFAULT_FAILED_MESSAGE = 'Failed to copy BuildHub inspiration items';

/**
 * Action to copy items from one mood board to another within a BuildHub project.
 */
export const action: ActionFunction = async ({ request, context }) => {
    const data = await request.formData();

    invariant(
        data.get('destinationBoardId'),
        'Destination moodboard id required for copying to.'
    );
    invariant(
        data.get('boardId'),
        'Source moodboard id required for copying from.'
    );
    invariant(data.get('pinListString'), 'pin ids required for copy.');
    invariant(data.get('buildHubUrl'), 'Moodboard BuildHub url required.');

    const pins = data.get('pinListString') as string;
    const variables: CopyInspirationItemsMutationVariables = {
        FromBoardID: Number(data.get('boardId')),
        ToBoardID: Number(data.get('destinationBoardId')),
        BuildHubUrl: data.get('buildHubUrl') as string,
        Pins: pins.split(','),
    };

    try {
        const { data: result } = await context.client.mutate<
            CopyInspirationItemsMutation,
            CopyInspirationItemsMutationVariables
        >({
            mutation: CopyInspirationItemsDocument,
            variables,
        });

        const validResult = result?.moodBoardPinsCopy;
        if (!validResult) {
            return json({
                buildHubUpsert: {
                    __typename: 'ServerError',
                    Message: DEFAULT_FAILED_MESSAGE,
                },
            });
        }

        return json(result);
    } catch (error) {
        return json({
            buildHubUpsert: {
                __typename: 'ServerError',
                Message:
                    error instanceof Error
                        ? error.message
                        : DEFAULT_FAILED_MESSAGE,
            },
        });
    }
};
